import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Copy, ExternalLink, Terminal, TriangleAlert, CircleCheck, Loader2 } from 'lucide-react'
import type { AgentStatusResponse } from '../../api/projectWriterApi'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agentId: string | null
  requestId: string | null
  status: AgentStatusResponse | null
  isRunning: boolean
  error: string | null
}

function statusLabel(status: AgentStatusResponse['status'] | null | undefined): string {
  if (!status) return 'starting'
  return status
    .split('_')
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ')
}

export function ProjectWriterRunDialog({
  open,
  onOpenChange,
  agentId,
  requestId,
  status,
  isRunning,
  error,
}: Props) {
  const events = status?.events ?? []
  const progress = status?.progress ?? 0
  const currentStep = status?.currentStep ?? 'Starting…'
  const runId = status?.runId ?? null
  const completed = status?.status === 'complete'
  const failed = status?.status === 'error' || !!error

  const [showJson, setShowJson] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const detailsText = useMemo(() => {
    const lines = [
      `agent_id: ${agentId ?? '—'}`,
      `run_id: ${runId ?? '—'}`,
      `request_id: ${requestId ?? '—'}`,
      `status: ${status?.status ?? '—'}`,
      `progress: ${typeof progress === 'number' ? progress : '—'}`,
      `step: ${currentStep}`,
    ]
    return lines.join('\n')
  }, [agentId, runId, requestId, status?.status, progress, currentStep])

  useEffect(() => {
    if (!open) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [open, events.length])

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // no-op
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {completed ? (
              <CircleCheck className="h-5 w-5 text-success" />
            ) : failed ? (
              <TriangleAlert className="h-5 w-5 text-destructive" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin" />
            )}
            Project Writer Run
            <Badge variant={completed ? 'default' : failed ? 'destructive' : 'secondary'}>
              {statusLabel(status?.status ?? (isRunning ? 'generating_content' : null))}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Live status + event timeline from the Project Writer agent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">{currentStep}</div>
              <div className="text-sm tabular-nums">{Math.min(100, Math.max(0, progress))}%</div>
            </div>
            <Progress value={Math.min(100, Math.max(0, progress))} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Agent ID</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={agentId ?? '—'} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={!agentId}
                  onClick={() => copy(agentId ?? '')}
                  title="Copy agent id"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Request ID (Cloudflare logs)</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={requestId ?? '—'} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={!requestId}
                  onClick={() => copy(requestId ?? '')}
                  title="Copy request id"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {failed && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <div className="font-medium text-destructive mb-1">Failure</div>
              <div className="text-destructive/90">
                {status?.error || error || 'Unknown error'}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm font-medium">Live events</div>
              <Badge variant="outline">{events.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => copy(detailsText)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy details
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowJson((v) => !v)}>
                {showJson ? 'Hide JSON' : 'Show JSON'}
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[320px] rounded-md border">
            <div className="p-3 space-y-2">
              {events.length === 0 ? (
                <div className="text-sm text-muted-foreground">No events yet…</div>
              ) : (
                events.map((evt, idx) => (
                  <div key={`${evt.ts}-${idx}`} className="flex items-start gap-3">
                    <div className="w-[110px] text-xs text-muted-foreground font-mono">
                      {new Date(evt.ts).toLocaleTimeString()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            evt.level === 'error'
                              ? 'destructive'
                              : evt.level === 'warn'
                                ? 'secondary'
                                : 'outline'
                          }
                          className="uppercase"
                        >
                          {evt.level}
                        </Badge>
                        <div className="text-sm">{evt.message}</div>
                      </div>
                      {evt.data && showJson && (
                        <pre className="mt-1 text-xs bg-muted/50 rounded p-2 overflow-auto">
                          {JSON.stringify(evt.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>

          {completed && status?.result?.previewUrl && (
            <div className="flex justify-end">
              <Button type="button" asChild>
                <a href={status.result.previewUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open preview
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ProjectWriterRunDialog

